<template>
	<div v-if="visible" class="fd-ea-container">
		<div class="fd-ea-first-row">
			<input type="text" v-model="ea" class="fd-ea tn fd-array-focus" :class="clazz"
				:style="composedStyle" :readonly="ro" :placeholder="placeholder"
				@keydown.enter.shift.exact.prevent="emit('array-before')"
				@keydown.enter.exact.prevent="emit('array-after')"
				@keydown.delete="deleteIfEmpty"/>
			<ea-action :meta="eaMetaInfo"><icon-button :name="eaIcon" :class="iconClass"></icon-button></ea-action>
			<more-opener v-if="hasTags" v-model="openMore"></more-opener>
		</div>
		<show-down v-if="hasTags" :show="openMore" class="fd-tags">
			<label v-for="tg in eap.tags"><input type="checkbox" class="fd-checkbox" @change="toggleTag(tg.value)" :checked="hasTag(tg.value)" :disabled="ro"> <span :style="tg.style">{{ tg.name }}</span></label>
		</show-down>
	</div>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "../Editor";
import type {EAddressPart} from "@t/fd/editor/EAddress";
import {computed, type CSSProperties, ref, watch} from "vue";
import MoreOpener from "../../helpers/MoreOpener.vue";
import type {EAMeta} from "./EAResolver";
import resolve from "./EAResolver";
import EaAction from "./EaAction.vue";
import ShowDown from "../../helpers/ShowDown.vue";
import IconButton from "../../helpers/IconButton.vue";

const emit = defineEmits(["array-before", "array-after", "array-delete"]);
const uprops = defineProps({...editorProps});

const props = uprops as EditorProps;
const eap = computed<EAddressPart>(() => props.part as EAddressPart);

const {visible, value, clazz, style, ro} = useEditor(props);

const openMore = ref<boolean>(false);

const hasTags = computed(() => {
    return Array.isArray(eap.value.tags) && eap.value.tags.length > 0;
});

const composedStyle = computed<(string | CSSProperties)[]>(() => {
    const s: (string | CSSProperties)[] = [];
    if (style.value) s.push(style.value);
    if (hasTags.value) {
        if (Array.isArray(value.value.tags)) {
            for (let tag of value.value.tags) {
                const ts = tagStyle(tag);
                if (ts) s.push(ts);
            }
        }
    }
    return s;
});

function tagStyle(tag: string): CSSProperties | null {
    if (Array.isArray(eap.value.tags)) {
        const eaTag = eap.value.tags.find((item) => item.value === tag);
        if (eaTag) return eaTag.style;
    }
    return null;
}

function hasTag(tag: string): boolean {
    if (Array.isArray(value.value.tags)) {
        return value.value.tags.indexOf(tag) >= 0;
    }
    return false;
}

function removeTag(tag: string) {
    if (Array.isArray(value.value.tags)) {
        const idx = value.value.tags.indexOf(tag);
        if (idx >= 0) value.value.tags.splice(idx, 1);
    }
}

function addTag(tag: string) {
    if (!Array.isArray(value.value.tags)) {
        value.value.tags = [tag];
        return;
    }
    const idx = value.value.tags.indexOf(tag);
    if (idx < 0) value.value.tags.push(tag);
}

function setTag(tag: string, isSet: boolean) {
    if (isSet) addTag(tag);
    else removeTag(tag);
}

function toggleTag(tag: string) {
    setTag(tag, !hasTag(tag));
}

const placeholder = computed(() => {
    const ph = eap.value.placeholder;
    return typeof ph === "string" ? ph : "";
});

const ea = computed({
    get() {
        return value.value.value;
    },
    set(v) {
        value.value.value = v;
    },
});

function deleteIfEmpty(e: Event) {
    if (typeof value.value.value === "string" && value.value.value.trim().length === 0) {
        e.preventDefault();
        emit('array-delete');
    }
}

const eaIcon = computed(() => {
    return eaMetaInfo.value.icon;
});

const iconClass = computed(() => {
    const c = ["fd-ea-icon"];
    const m = eaMetaInfo.value;
    if (!m.ok) c.push("fd-ea-icon-warning");
    else if (m.empty) c.push("fd-ea-icon-empty");
    return c;
});


const eaMetaInfo = computed((): EAMeta => {
    return resolve(ea.value);
});

watch(() => eaMetaInfo.value.type, (type) => {
	value.value.type = type;
});
</script>

<style>
.fd-ea-container {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	row-gap: 4px;
}

.fd-ea-first-row {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	column-gap: 0;
	width: 100%;
}

.fd-ea-icon {
	/* color: var(--active); */
	margin-left: 2px;
}

.fd-ea-icon.fd-ea-icon-warning {
	color: var(--orange);
}

.fd-ea-icon.fd-ea-icon-empty {
	color: var(--border-color);
}

.fd-ea {
	width: 100%;
}

.fd-tags {
	display: flex;
	flex-direction: column;
	padding-left: var(--input-h-padding); /* inset for icon */
	font-size: var(--font-size-s);
}
.fd-tags .fd-checkbox {
	margin-right: 0;
}
</style>
